import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, fmtDate, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BellRing, XCircle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function FollowupsDialog({ open, onOpenChange, onChanged }) {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/leads/followups-pending").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const addNote = async (l) => {
    const text = (notes[l.id] || "").trim();
    if (!text) { toast.error("Enter a note first"); return; }
    try {
      await api.post(`/leads/${l.id}/activity`, { kind: "note", text });
      toast.success("Note added — follow-up clock reset");
      setNotes((n) => ({ ...n, [l.id]: "" }));
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markLost = async (l) => {
    const reason = window.prompt(`Mark "${l.company_name || l.number}" as Lost — reason?`, "No response");
    if (reason === null) return;
    try {
      await api.post(`/leads/${l.id}/mark-lost`, { reason });
      toast.success("Lead marked Lost");
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="followups-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing size={17} className="text-[#8B5CF6]" /> Pending Follow-ups
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">
          Leads silent for <b>3+ working days</b> (Mon–Sat). Adding a note resets the clock; leads silent <b>12+ working days</b> can be marked Lost.
        </p>
        <div className="space-y-2.5">
          {loading && <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-400" /></div>}
          {!loading && rows.map((l) => (
            <div key={l.id} className={`border rounded-xl p-3 ${l.can_mark_lost ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`} data-testid={`fu-lead-${l.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="text-sm font-semibold text-slate-800">{l.company_name || l.contact_person}</span>
                  <span className="ml-2 text-[10px] font-mono text-slate-400">{l.number}</span>
                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{l.stage}</span>
                </div>
                <span className={`flex-none text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${l.can_mark_lost ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`} data-testid={`fu-silent-${l.id}`}>
                  {l.days_silent} working day{l.days_silent === 1 ? "" : "s"} silent
                </span>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                {l.value > 0 && <>Value {inr(l.value)} · </>}Last touch {fmtDate(l.last_touch)}{l.mobile ? ` · ${l.mobile}` : ""}
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                <Input value={notes[l.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [l.id]: e.target.value }))}
                       onKeyDown={(e) => e.key === "Enter" && addNote(l)}
                       placeholder="Add a follow-up note (e.g. called, awaiting reply)…"
                       className="h-9 text-xs flex-1" data-testid={`fu-note-input-${l.id}`} />
                <Button size="sm" className="h-9 bg-[#3B82F6] hover:bg-[#2563EB] text-white" onClick={() => addNote(l)} data-testid={`fu-note-save-${l.id}`}>
                  Add Note
                </Button>
                {l.can_mark_lost && (
                  <Button size="sm" variant="outline" className="h-9 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => markLost(l)} data-testid={`fu-mark-lost-${l.id}`}>
                    <XCircle size={13} className="mr-1" /> Mark Lost
                  </Button>
                )}
                <Link to="/leads" onClick={() => onOpenChange(false)} className="p-2 text-slate-400 hover:text-[#3B82F6]" title="Open in Leads" data-testid={`fu-open-${l.id}`}>
                  <ExternalLink size={14} />
                </Link>
              </div>
            </div>
          ))}
          {!loading && rows.length === 0 && (
            <div className="text-center py-10 text-sm text-slate-500" data-testid="fu-empty">All caught up — no leads waiting on a follow-up.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
